import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LightningBoltIcon, StarFilledIcon, CheckIcon, Cross2Icon } from "@radix-ui/react-icons";
import { TbMoneybag, TbFlame } from "react-icons/tb";
import { toast, TypeOptions } from "react-toastify";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { dasApi, DasApiInterface } from "@metaplex-foundation/digital-asset-standard-api";
import { mplBubblegum, getAssetWithProof, transfer } from "@metaplex-foundation/mpl-bubblegum";
import { mplToolbox } from "@metaplex-foundation/mpl-toolbox";
import { mplCore } from "@metaplex-foundation/mpl-core";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";
import { mplTokenMetadata } from "@metaplex-foundation/mpl-token-metadata";
import { Umi, publicKey } from "@metaplex-foundation/umi";
import { Transaction } from "@solana/web3.js";
import { fromWeb3JsPublicKey, toWeb3JsInstruction } from "@metaplex-foundation/umi-web3js-adapters";
import { PriceType, NftDataType, CollectionType } from "../../lib/types";
import { createPurchaseTx } from "../../lib/util";
import { checkPriceAvailable, purchaseTraitByApi, generatePreview } from "../../lib/api";
import SelectBurnNFTDialog from "../admin/dialog/SelectBurnNFTDialog";
import SelectNftDialog from "../admin/dialog/SelectNftDialog";
import OverlaySuccessCard from "./OverlaySuccessCard";

interface TraitCardProps {
    trait: PriceType;
    index: number;
    selectedBooby: Record<number, string>;
    onSelectBooby: (index: number) => void;
    onPurchase: (index: number) => void;
    coinRef: any;
    AllFilteredNFTs: NftDataType[];
    selectedCollection: CollectionType;
    nftLoading: boolean;
    getPrices: () => void;
    fetchWalletNfts: () => void;
}

export const TraitCard: React.FC<TraitCardProps> = ({
    trait,
    index,
    selectedBooby,
    onSelectBooby,
    onPurchase,
    coinRef,
    AllFilteredNFTs,
    selectedCollection,
    nftLoading,
    getPrices,
    fetchWalletNfts,
}) => {
    const [burning, setBurning] = useState(false);
    const [goldCoin, setGoldCoin] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [burnDialogOpen, setBurnDialogOpen] = useState(false);
    const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
    const [selectedURL, setSelectedURL] = useState<string | undefined>(undefined);
    const [selectedUpgradeNFT, setSelectedUpgradeNFT] = useState<NftDataType | undefined>(undefined);
    const [selectedBurnNFT, setSelectedBurnNFT] = useState<NftDataType | undefined>(undefined);
    const isSelected = Boolean(selectedBooby[index]);
    const isUnlimited = trait.items === -1;
    const { connection } = useConnection();
    const wallet = useWallet();
    const umi = useMemo(
        () => {
            const instance = createUmi(`https://mainnet.helius-rpc.com/?api-key=${process.env.NEXT_PUBLIC_HELIUS_API}`);
            return instance
                .use(dasApi())
                .use(mplBubblegum())
                .use(mplToolbox())
                .use(mplCore())
                .use(walletAdapterIdentity(wallet, true))
                .use(mplTokenMetadata()) as Umi & { rpc: DasApiInterface };
        },
        [wallet],
    );
    const [filteredBurnNFTs, setFilteredBurnNFTs] = useState<NftDataType[]>([]);
    const [filteredUpgradeNFTs, setFilteredUpgradeNFTs] = useState<NftDataType[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [showSuccessCard, setShowSuccessCard] = useState(false);

    const meetsRequirements = useMemo(() => {
        if (!trait.traitRequirements || trait.traitRequirements.length === 0) {
            return true;
        }

        if (trait.burnRequirement) {
            if (!selectedBurnNFT) {
                return false;
            }

            return trait.traitRequirements.every(requirement => {
                const hasTrait = selectedBurnNFT.attributes?.some(attr =>
                    attr.trait_type === requirement.trait_type &&
                    attr.value === requirement.trait_value
                );

                return hasTrait;
            });
        }
        else {
            if (!selectedUpgradeNFT) {
                return false;
            }

            return trait.traitRequirements.every(requirement => {
                const hasTrait = selectedUpgradeNFT.attributes?.some(attr =>
                    attr.trait_type === requirement.trait_type &&
                    attr.value === requirement.trait_value
                );

                return hasTrait;
            });
        }
    }, [trait.traitRequirements, trait.burnRequirement, selectedBurnNFT, selectedUpgradeNFT]);

    useEffect(() => {
        if (burning) {
            document.body.offsetHeight;
        }
    }, [burning]);

    const handleBurnPurchase = useCallback(async () => {
        setBurning(true);
        setGoldCoin(true);
        onPurchase(index);

        setTimeout(() => {
            setBurning(false);
            setGoldCoin(false);
        }, 4000);

        if (!trait) {
            toast("Please select a Trait first");
            return;
        }
        if (!wallet || !wallet.connected || !wallet.publicKey) {
            toast("Please connect wallet first");
            return;
        }
        if (!selectedBurnNFT) {
            toast("Please select a Boobie to burn first");
            return;
        }
        if (!selectedUpgradeNFT) {
            toast("Please select a Boobie to upgrade first");
            return;
        }

        if (!meetsRequirements) {
            toast("Selected Boobie does not meet the trait requirements");
            return;
        }

        const assetWithProof = await getAssetWithProof(umi, publicKey(selectedBurnNFT?.mint!), { truncateCanopy: true });
        const transferTranscation = transfer(umi, {
            ...assetWithProof,
            leafOwner: fromWeb3JsPublicKey(wallet.publicKey),
            newLeafOwner: publicKey("4bUqST9EWWo4fTYwMjmsAEhh9uXzj3eALFL7h7aGFbaG"),
        })

            .getInstructions();

        if (!transferTranscation) {
            toast.error("User cancelled operation!");
            return;
        }


        let initialTxn: string = "";
        let itemType: string = "";
        let itemId: number = 0;
        let walletAddress: string = "";
        let transactionTimestamp: number = 0;
        try {
            toast("Buying Trait..");

            let result = await checkPriceAvailable(trait.id);

            if (!result.result) {
                toast(String(result.data),);

                return;
            }
            result = await createPurchaseTx(
                trait,
                umi,
                wallet,
                connection
            );

            if (!result.result) {
                toast(String(result.data),);

                return;
            }
            let tx = result.data as Transaction;

            const { blockhash, lastValidBlockHeight } =
                await connection.getLatestBlockhash("confirmed");
            tx.feePayer = wallet.publicKey;
            tx.recentBlockhash = blockhash;
            tx.lastValidBlockHeight = lastValidBlockHeight;
            transferTranscation.forEach(instruction => {
                tx.instructions.push(toWeb3JsInstruction(instruction));
            });

            const txId = await wallet.sendTransaction(tx, connection, {
                skipPreflight: true,
                maxRetries: 5,
            });

            const _transactionTimestamp = Math.floor(Date.now() / 1000);

            initialTxn = txId;
            itemType = "trait";
            itemId = trait.id;
            walletAddress = wallet.publicKey.toString();
            transactionTimestamp = _transactionTimestamp;
            console.log(
                "selectedUpgradeNFT ", selectedUpgradeNFT?.mint,
                "trait ", trait,
                "walletAddress ", walletAddress,
                "transactionTimestamp ", transactionTimestamp,
                "initialTxn ", initialTxn,
                "itemType ", itemType,
                "itemId ", itemId,
                "walletAddress ", walletAddress,);

            toast("Updating Boobie ✨");
            const status = await purchaseTraitByApi(
                wallet.publicKey.toString(),
                trait,
                txId,
                selectedUpgradeNFT?.mint!,
                () => { },
                (msg: string, type: TypeOptions) => toast(msg),
                getPrices
            );

            await fetchWalletNfts();
            setShowSuccessCard(true);
            toast.success("Congratulations! Your Boobie is styling a new look! ✨");

        } catch (e) {
            let error = (e as Error).toString();
            if (error.split(":")[1].split(".")[0].length > 0) {
                console.log("> ", error.split(":")[1].split(".")[0].length > 0);
                toast(error.split(":")[1].split(".")[0]);
            }
            console.log(error);
            console.log(JSON.stringify(e as any));

        }

    }, [index, onPurchase, selectedBurnNFT, selectedUpgradeNFT, trait, wallet, umi, connection, fetchWalletNfts, getPrices, meetsRequirements]);

    const handlePurchase = useCallback(async () => {
        setGoldCoin(true);
        onPurchase(index);

        setTimeout(() => {
            setGoldCoin(false);
        }, 3000);

        if (!trait) {
            toast("Please select a Trait first");
            return;
        }
        if (!wallet || !wallet.connected || !wallet.publicKey) {
            toast("Please connect wallet first");
            return;
        }
        if (!(selectedUpgradeNFT)) {
            toast("Please select a Boobie first");
            return;
        }

        if (!meetsRequirements) {
            toast("Selected Boobie does not meet the trait requirements");
            return;
        }

        let initialTxn: string = "";
        let itemType: string = "";
        let itemId: number = 0;
        let walletAddress: string = "";
        let transactionTimestamp: number = 0;
        try {
            toast("Buying Trait..");

            let result = await checkPriceAvailable(trait.id);

            if (!result.result) {
                toast(String(result.data),);

                return;
            }
            result = await createPurchaseTx(
                trait,
                umi,
                wallet,
                connection
            );

            if (!result.result) {
                toast(String(result.data),);

                return;
            }
            let tx = result.data as Transaction;

            const { blockhash, lastValidBlockHeight } =
                await connection.getLatestBlockhash("confirmed");
            tx.feePayer = wallet.publicKey;
            tx.recentBlockhash = blockhash;
            tx.lastValidBlockHeight = lastValidBlockHeight;

            const txId = await wallet.sendTransaction(tx, connection, {
                skipPreflight: true,
                maxRetries: 5,
            });

            const _transactionTimestamp = Math.floor(Date.now() / 1000);

            initialTxn = txId;
            itemType = "trait";
            itemId = trait.id;
            walletAddress = wallet.publicKey.toString();
            transactionTimestamp = _transactionTimestamp;

            toast("Updating Boobie ✨");
            const status = await purchaseTraitByApi(
                wallet.publicKey.toString(),
                trait,
                txId,
                selectedUpgradeNFT?.mint!,
                () => { },
                (msg: string, type: TypeOptions) => toast(msg),
                getPrices
            );

            await fetchWalletNfts();
            setShowSuccessCard(true);
            toast.success("Congratulations! Your Boobie is styling a new look! ✨");

        } catch (e) {
            let error = (e as Error).toString();
            if (error.split(":")[1].split(".")[0].length > 0) {
                console.log("> ", error.split(":")[1].split(".")[0].length > 0);
                toast.error(error.split(":")[1].split(".")[0]);
            }
            console.log(error);
            console.log(JSON.stringify(e as any));

        }
    }, [index, onPurchase, meetsRequirements]);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);

    const handleSelectBooby = useCallback(() => {
        onSelectBooby(index);
    }, [index, onSelectBooby]);

    const handleBurnSelectBooby = useCallback(() => {
        setBurnDialogOpen(true);
    }, []);

    const handleBurnDialogClose = useCallback(() => {
        setBurnDialogOpen(false);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedUpgradeNFT != undefined) {
                const response = await generatePreview(
                    (selectedUpgradeNFT)!.mint,
                    "trait",
                    trait.id.toString()
                );
                setSelectedURL(response);
            }
        };

        fetchData();
    }, [selectedBurnNFT, selectedUpgradeNFT]);

    const handleBurnNftSelect = useCallback((nft: NftDataType) => {
        if (selectedBurnNFT?.mint === nft.mint) {
            setSelectedBurnNFT(undefined);
            setSelectedURL(undefined);
            return;
        }

        setSelectedBurnNFT(nft);
        onSelectBooby(index);
        setBurnDialogOpen(false);
    }, [index, onSelectBooby, selectedBurnNFT, trait.traitRequirements]);

    const handleUpgradeNftSelect = useCallback((nft: NftDataType) => {
        if (selectedUpgradeNFT?.mint === nft.mint) {
            setSelectedUpgradeNFT(undefined);
            setSelectedURL(undefined);
            return;
        }
        setSelectedUpgradeNFT(nft);
        onSelectBooby(index);
        setUpgradeDialogOpen(false);
    }, [index, onSelectBooby, selectedUpgradeNFT]);

    const handleUpgradeDialogClose = useCallback(() => {
        setUpgradeDialogOpen(false);
    }, []);

    useEffect(() => {
        const newFilteredBurnNFTs = AllFilteredNFTs.filter(nft =>
            !selectedUpgradeNFT || nft.mint !== selectedUpgradeNFT.mint
        );
        const newFilteredUpgradeNFTs = AllFilteredNFTs.filter(nft =>
            !selectedBurnNFT || nft.mint !== selectedBurnNFT.mint
        );
        setFilteredBurnNFTs(newFilteredBurnNFTs);
        setFilteredUpgradeNFTs(newFilteredUpgradeNFTs);
    }, [AllFilteredNFTs, selectedUpgradeNFT, selectedBurnNFT]);

    return (
        <motion.div
            className={`glass-panel relative overflow-hidden rounded-xl border border-white/10 transition-all duration-300 ${isSelected ? 'ring-2 ring-brand-cyan' : ''} ${burning ? 'ring-2 ring-red-500' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="flex flex-col h-full">
                <div className="relative aspect-square overflow-hidden rounded-t-xl bg-black/50">
                    <motion.img
                        src={selectedURL || trait.trait?.blob_url}
                        alt={trait.trait?.trait_value}
                        className={`w-full h-full object-cover ${burning ? 'sepia contrast-125 brightness-75' : ''}`}
                        animate={{ scale: isHovered ? 1.05 : 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    />

                    <motion.div
                        className="absolute top-2 right-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded-md text-xs font-bold text-white border border-white/10"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {isUnlimited ? "Unlimited" : `${trait.items}/${trait.total}`}
                    </motion.div>

                    {trait.burnRequirement && (
                        <motion.div
                            className="absolute top-2 left-2 px-2 py-1 bg-red-500/80 backdrop-blur-md rounded-md text-xs font-bold text-white flex items-center gap-1"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <LightningBoltIcon className="w-3 h-3" />
                            <span>Burn {trait.burnRequirement}</span>
                        </motion.div>
                    )}

                    {!trait.burnRequirement && (
                        <motion.div
                            className="absolute top-2 left-2 px-2 py-1 bg-brand-purple/80 backdrop-blur-md rounded-md text-xs font-bold text-white flex items-center gap-1"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <StarFilledIcon className="w-3 h-3" />
                            <span>$BOOB Only</span>
                        </motion.div>
                    )}

                    <AnimatePresence>
                        {goldCoin && (
                            <motion.div
                                className="absolute inset-0 pointer-events-none z-20"
                                ref={coinRef}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                {/* Placeholder for coin animation */}
                                <div className="absolute inset-0 bg-yellow-500/20 animate-pulse" />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {burning && (
                            <>
                                <motion.div
                                    className="absolute inset-0 bg-red-500/30 mix-blend-overlay z-10"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                />
                            </>
                        )}
                    </AnimatePresence>
                </div>

                <div className="p-4 flex flex-col gap-3 flex-1">
                    <motion.h3
                        className="text-lg font-bold text-white"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        {trait.trait?.trait_value}
                    </motion.h3>

                    {trait.traitRequirements && trait.traitRequirements.length > 0 && (
                        <motion.div
                            className="text-xs text-gray-400"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className="flex flex-col gap-1">
                                {trait.traitRequirements.map((req, idx) => {
                                    const selectedNFT = trait.burnRequirement ? selectedBurnNFT : selectedUpgradeNFT;
                                    const actualTrait = selectedNFT?.attributes?.find(
                                        attr => attr.trait_type === req.trait_type
                                    );

                                    const meetsThisRequirement = selectedNFT?.attributes?.some(
                                        attr => attr.trait_type === req.trait_type &&
                                            attr.value === req.trait_value
                                    );

                                    return (
                                        <div key={idx} className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                {meetsThisRequirement ? (
                                                    <CheckIcon className="text-green-500 w-3 h-3" />
                                                ) : (
                                                    <Cross2Icon className="text-red-500 w-3 h-3" />
                                                )}
                                                <span>
                                                    Requires: {req.trait_type} - {req.trait_value}
                                                </span>
                                            </div>
                                            {selectedNFT && actualTrait && (
                                                <div className="flex items-center gap-2 ml-5">
                                                    <span className={actualTrait.value === req.trait_value ? 'text-brand-cyan' : 'text-amber-500'}>
                                                        Current: {actualTrait.trait_type} - {actualTrait.value}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}

                    <motion.div
                        className="flex items-center gap-2 text-brand-cyan font-medium mt-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <TbMoneybag className="w-5 h-5" />
                        <span>{trait.price.toLocaleString()} {trait.token?.name}</span>
                    </motion.div>
                </div>

                <div className="p-4 pt-0 flex flex-col gap-2">
                    {trait.burnRequirement && (
                        <motion.button
                            className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${selectedBurnNFT ? 'bg-brand-purple/20 text-brand-purple border border-brand-purple/50' : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'}`}
                            onClick={() => setBurnDialogOpen(true)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            {selectedBurnNFT ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="px-1.5 py-0.5 bg-brand-purple text-white text-[10px] rounded uppercase font-bold">Selected</span>
                                    <span className="truncate max-w-[150px]">{selectedBurnNFT.projectname + " #" + selectedBurnNFT.nftname || `Booby #${selectedBurnNFT.mint.slice(0, 4)}`}</span>
                                </span>
                            ) : (
                                "Select Booby to Burn"
                            )}
                        </motion.button>
                    )}

                    <motion.button
                        className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${selectedUpgradeNFT ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/50' : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'}`}
                        onClick={() => setUpgradeDialogOpen(true)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        {selectedUpgradeNFT ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="px-1.5 py-0.5 bg-brand-cyan text-black text-[10px] rounded uppercase font-bold">Selected</span>
                                <span className="truncate max-w-[150px]">{selectedUpgradeNFT.projectname + " #" + selectedUpgradeNFT.nftname || `Booby #${selectedUpgradeNFT.mint.slice(0, 4)}`}</span>
                            </span>
                        ) : (
                            "Select Booby for Upgrade"
                        )}
                    </motion.button>

                    <div className="relative">
                        <motion.button
                            className={`w-full py-3 rounded-lg font-bold text-white shadow-lg flex items-center justify-center gap-2 transition-all ${
                                trait.burnRequirement 
                                    ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 shadow-red-900/20' 
                                    : 'bg-gradient-to-r from-brand-cyan to-brand-purple hover:from-brand-cyan/90 hover:to-brand-purple/90 shadow-brand-purple/20'
                            } disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                            onClick={trait.burnRequirement ? handleBurnPurchase : handlePurchase}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            disabled={
                                trait.burnRequirement
                                    ? !selectedBurnNFT || !selectedUpgradeNFT || !meetsRequirements
                                    : !selectedUpgradeNFT || !meetsRequirements
                            }
                        >
                            {trait.burnRequirement ? (
                                <>
                                    <TbFlame className="w-5 h-5" />  Burn & Purchase
                                </>
                            ) : (
                                <>
                                    <StarFilledIcon className="w-4 h-4" /> Purchase with $BOOB
                                </>
                            )}
                        </motion.button>
                    </div>
                </div>
            </div>

            <SelectBurnNFTDialog
                dialogOpen={burnDialogOpen}
                selectedNft={selectedBurnNFT}
                nftLoading={nftLoading}
                AllFilteredNFTs={filteredBurnNFTs}
                selectedCollection={selectedCollection}
                selectNftHandle={handleBurnNftSelect}
                handleDialogOpen={setBurnDialogOpen}
                handleClose={handleBurnDialogClose}
            />

            <SelectNftDialog
                dialogOpen={upgradeDialogOpen}
                selectedNft={selectedUpgradeNFT}
                nftLoading={nftLoading}
                AllFilteredNFTs={filteredUpgradeNFTs}
                selectedCollection={selectedCollection}
                selectNftHandle={handleUpgradeNftSelect}
                handleDialogOpen={setUpgradeDialogOpen}
                handleClose={handleUpgradeDialogClose}
            />
            {selectedUpgradeNFT && (
                <OverlaySuccessCard
                    open={showSuccessCard}
                    onOpenChange={setShowSuccessCard}
                    overlay={{
                        name: selectedUpgradeNFT?.projectname + " #" + selectedUpgradeNFT?.nftname,
                        image_url: selectedURL ?? "",
                        background: trait.trait?.trait_type === "Background" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "Background")?.value ?? "",
                        basefeathers: trait.trait?.trait_type === "BaseFeathers" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "BaseFeathers")?.value ?? "",
                        headaccessories: trait.trait?.trait_type === "HeadAccessories" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "HeadAccessories")?.value ?? "",
                        eyes: trait.trait?.trait_type === "Eyes" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "Eyes")?.value ?? "",
                        accessories: trait.trait?.trait_type === "Accessories" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "Accessories")?.value ?? "",
                        mouth: trait.trait?.trait_type === "Mouth" ? trait.trait.trait_value : selectedUpgradeNFT?.attributes?.find(attr => attr.trait_type === "Mouth")?.value ?? "",
                        id: selectedUpgradeNFT?.mint ?? "",
                    }}
                />
            )}
        </motion.div>
    );
};
